import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { RUN_STATUS_LABELS, procedureErrorMessage } from "../../lib/procedures";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronRight, ClipboardCheck, Loader2, Plus } from "lucide-react";


// « Procédures » card of a mission: what the type asks, what has started, progress and anomalies. `onLoaded` hands the
// list to the page (the finish dialog needs the required procedures still open). Rights come from the server's `capabilities`.
export default function MissionProceduresCard({ mission, onLoaded }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", data: null, error: "" });
  const [busy, setBusy] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/missions/${mission.id}/checklists`);
      setState({ phase: "ready", data, error: "" });
      onLoaded?.(data);
    } catch (e) {
      setState({ phase: "error", data: null, error: procedureErrorMessage(e, "Impossible de charger les procédures.") });
    }
  }, [mission.id, onLoaded]);
  // The mission's status decides what can start: re-read when it changes.
  useEffect(() => { load(); }, [load, mission.status]);

  const start = async (templateId) => {
    setBusy(templateId);
    try {
      const { data } = await api.post(`/missions/${mission.id}/checklists`, { template_id: templateId });
      navigate(`/missions/${mission.id}/procedures/${data.id}`);
    } catch (e) {
      toast.error(procedureErrorMessage(e, "Impossible de démarrer la procédure."));
      setBusy(null);
    }
  };

  if (state.phase === "loading") return <Card className="p-5"><Skeleton className="h-16 w-full" /></Card>;
  if (state.phase === "error") {
    return (
      <Card className="p-5 text-sm text-slate-600 flex items-center gap-2 flex-wrap" data-testid="mission-procedures">
        {state.error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
      </Card>
    );
  }
  const { items, missing_required: missing, capabilities: caps } = state.data;
  if (items.length === 0 && !caps.add) return null;
  const canStartType = caps.start && mission.status === "in_progress";

  return (
    <Card className="p-5 space-y-3" data-testid="mission-procedures">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">Procédures</h3>
        {caps.add && <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="mission-procedure-add"><Plus size={14} className="mr-1.5" />Ajouter</Button>}
      </div>
      {missing.length > 0 && mission.status === "in_progress" && (
        <p className="flex items-start gap-1.5 text-sm text-amber-800" data-testid="mission-procedures-missing">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          À terminer avant la fin de la mission : {missing.join(", ")}.
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune procédure pour cette mission.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((p) => (
            <ProcedureRow key={p.template_id} item={p} missionId={mission.id} canStart={canStartType || caps.add}
              busy={busy === p.template_id} onStart={() => start(p.template_id)} />
          ))}
        </ul>
      )}
      <AddProcedureDialog open={addOpen} onOpenChange={setAddOpen} present={items.map((i) => i.template_id)} onAdd={start} />
    </Card>
  );
}

function ProcedureRow({ item, missionId, canStart, busy, onStart }) {
  const run = item.run;
  const progress = run?.progress;
  const done = run?.status === "completed";
  const body = (
    <>
      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-slate-500">
        {done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <ClipboardCheck size={16} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 break-words">{item.label}</span>
          <span className="text-xs text-slate-500">{item.required ? "obligatoire" : "facultative"}</span>
        </div>
        <div className="text-xs text-slate-500">
          {!run ? "Pas encore démarrée" : `${RUN_STATUS_LABELS[run.status]} · ${progress.answered} / ${progress.total} élément${progress.total > 1 ? "s" : ""}`}
          {progress?.anomalies > 0 && <span className="text-amber-700"> · {progress.anomalies} anomalie{progress.anomalies > 1 ? "s" : ""}</span>}
        </div>
      </div>
    </>
  );
  if (run) {
    return (
      <li>
        <Link to={`/missions/${missionId}/procedures/${run.id}`} className="flex items-center gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md"
          data-testid={`mission-procedure-${item.template_id}`}>
          {body}<ChevronRight size={16} className="text-slate-300 shrink-0" />
        </Link>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`mission-procedure-${item.template_id}`}>
      {body}
      {canStart && (
        <Button size="sm" onClick={onStart} disabled={busy}  data-testid={`mission-procedure-start-${item.template_id}`}>
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Démarrer
        </Button>
      )}
    </li>
  );
}

// Organisers add an optional procedure (any active, published one not already on the mission).
function AddProcedureDialog({ open, onOpenChange, present, onAdd }) {
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChoice("");
    setBusy(false);
    setOptions(null);
    api.get("/procedures")
      .then(({ data }) => setOptions(data.items.filter((p) => p.active && p.current_version && !present.includes(p.id))))
      .catch(() => setOptions("error"));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setBusy(true);
    await onAdd(choice);
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-procedure-add-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une procédure</DialogTitle>
          <DialogDescription>Elle sera facultative pour cette mission et suivra sa version publiée actuelle.</DialogDescription>
        </DialogHeader>
        {options === null ? <Skeleton className="h-10 w-full" /> : options === "error" ? (
          <p className="text-sm text-red-700">Impossible de charger les procédures.</p>
        ) : options.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune autre procédure publiée.</p>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="procedure-add-select">Procédure</Label>
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger id="procedure-add-select" data-testid="mission-procedure-add-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{options.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={add} disabled={!choice || busy}  data-testid="mission-procedure-add-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ajouter et ouvrir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
